import { Fragment, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Prose from '../components/Prose';
import Reveal from '../components/Reveal';
import SiteImage from '../components/SiteImage';
import Spinner from '../components/Spinner';
import { api, type ApiError, type Page } from '../lib/api';
import NotFound from './NotFound';

/**
 * Pages laid out after the printed "An Introduction to FTTMY". Their text stays
 * in the CMS, set in the brochure style (see `.prose.brochure`); the photo is
 * part of the design and closes the page.
 */
type Photo = { src: string; width: number; height: number; alt: string };
// `photoApart`: the closing photo stands on its own below the quotations
// instead of rising pale behind the last one. `plain`: the page keeps the
// standard page style and only gains the closing photo, rising pale behind
// its last lines. `sectionPhotos`: rows of photos placed after given sections
// of the text (split before each section title and the closing hymn), keyed by
// the section they follow. `closing`: the closing hymn set in a panel over a
// pale photo, with photos stacked above the verse.
const BROCHURE_PAGES: Record<
  string,
  {
    photo?: Photo;
    gallery?: Photo[];
    photoApart?: boolean;
    plain?: boolean;
    sectionPhotos?: Record<number, Photo[][]>;
    closing?: { background: Photo; rows?: Photo[][]; photos: Photo[] };
  }
> = {
  burden: {
    plain: true,
    sectionPhotos: {
      // After "Training Makes a Difference".
      1: [
        [
          { src: '/media/burden/burden1', width: 1037, height: 514, alt: 'FTTMY trainees' },
          { src: '/media/burden/burden2', width: 1500, height: 608, alt: 'FTTMY trainees' },
          { src: '/media/burden/burden3', width: 1500, height: 760, alt: 'FTTMY trainees' },
        ],
      ],
    },
    // As the brochure's last page.
    closing: {
      background: { src: '/media/burden/burden10', width: 1768, height: 877, alt: '' },
      // The earlier classes, smaller, above the two latest.
      rows: [
        [
          { src: '/media/burden/burden4', width: 1500, height: 554, alt: 'FTTMY trainees' },
          { src: '/media/burden/burden5', width: 1203, height: 664, alt: 'FTTMY trainees' },
        ],
        [
          { src: '/media/burden/burden6', width: 1500, height: 629, alt: 'FTTMY trainees' },
          { src: '/media/burden/burden7', width: 1500, height: 685, alt: 'FTTMY trainees' },
        ],
      ],
      photos: [
        { src: '/media/burden/burden8', width: 1500, height: 538, alt: 'FTTMY trainees' },
        { src: '/media/burden/burden9', width: 1470, height: 523, alt: 'FTTMY trainees at the graduation meeting, 24 August 2024' },
      ],
    },
  },
  'purpose-goal': {
    photo: { src: '/media/purpose/purpose', width: 1680, height: 600, alt: 'FTTMY trainees and trainers' },
  },
  truths: {
    photo: { src: '/media/truths/truth3', width: 1890, height: 911, alt: 'Trainees in class' },
    gallery: [
      { src: '/media/truths/truth1', width: 786, height: 1000, alt: 'A set of ministry books' },
      { src: '/media/truths/truth2', width: 1392, height: 1000, alt: 'The Economy of God and other books on a desk' },
    ],
  },
  life: {
    photo: { src: '/media/life/life2', width: 1200, height: 504, alt: 'Sisters reading together' },
    gallery: [{ src: '/media/life/life1', width: 900, height: 675, alt: 'Brothers praying together' }],
  },
  service: {
    photo: { src: '/media/service/service3', width: 900, height: 675, alt: 'Trainees with the children' },
    gallery: [
      { src: '/media/service/service1', width: 600, height: 450, alt: 'Trainees in fellowship' },
      { src: '/media/service/service5', width: 1280, height: 621, alt: 'Trainees in fellowship at an outdoor table' },
      { src: '/media/service/service6', width: 1500, height: 830, alt: 'Sisters sharing a meal together' },
    ],
  },
  'an-open-letter': {
    photo: { src: '/media/prepare/prepare', width: 1350, height: 1013, alt: 'FTTMY trainees cheering together' },
    plain: true,
  },
  // Its quotations take the brochure style; the book covers sit in the course
  // cards themselves (page content).
  courses: { plain: true },
  character: {
    photo: { src: '/media/character/character3', width: 2081, height: 758, alt: 'FTTMY trainees' },
    photoApart: true,
    gallery: [
      { src: '/media/character/character1', width: 900, height: 579, alt: 'Brothers in the classroom' },
      { src: '/media/character/character2', width: 900, height: 607, alt: 'Sisters studying in class' },
    ],
  },
};

/**
 * Rows of whole photos with lightly faded edges. On wider screens every photo
 * in a block has the same height and each row is centred, so the rows are
 * symmetric however the photos' shapes differ (a row that is too wide wraps,
 * still centred). On phones each photo takes the full width, one under another.
 */
function PhotoRows({ rows, className = '' }: { rows: Photo[][]; className?: string }) {
  // Rows of three are kept a little lower so they fit across.
  const height = rows.some((r) => r.length > 2) ? 'sm:h-28 md:h-32 lg:h-36 xl:h-40' : 'sm:h-32 md:h-40 lg:h-44 xl:h-48';
  return (
    <Reveal className={`space-y-4 sm:space-y-5 ${className}`}>
      {rows.map((row) => (
        <div key={row[0].src} className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-5">
          {row.map((p) => (
            <SiteImage
              key={p.src}
              src={p.src}
              width={p.width}
              height={p.height}
              alt={p.alt}
              sizes="(min-width: 640px) 36rem, 100vw"
              className={`h-auto w-full sm:w-auto ${height} mask-x-from-94% mask-y-from-94%`}
            />
          ))}
        </div>
      ))}
    </Reveal>
  );
}

/**
 * A page's closing panel, as the brochure's last page: a pale photo behind,
 * every class photo in an even grid, then the verse.
 */
function ClosingPanel({
  background,
  rows,
  photos,
  html,
}: {
  background: Photo;
  rows?: Photo[][];
  photos: Photo[];
  html: string;
}) {
  return (
    <Reveal className="mt-16 lg:mt-20">
      <section className="relative isolate overflow-hidden rounded-2xl px-3 pb-6 pt-6 sm:rounded-3xl sm:px-10 sm:pb-12 sm:pt-12 lg:pb-16 lg:pt-16">
        <SiteImage
          src={background.src}
          width={background.width}
          height={background.height}
          alt=""
          sizes="(min-width: 1024px) 80rem, 100vw"
          className="absolute inset-0 -z-10 h-full w-full object-cover object-bottom"
        />
        {/* A light white wash, so the building shows clearly behind the photos
            and the verse while keeping its colours soft. */}
        <div aria-hidden className="absolute inset-0 -z-10 bg-linear-to-b from-white/45 via-white/20 via-50% to-white/55" />
        {/* All the classes as an even grid of equal, borderless tiles, oldest
            first. Each photo fills its tile; a taller one loses a little at the
            bottom (chairs and floor), never the heads at the top. */}
        <ul className="m-0 grid list-none grid-cols-2 gap-2.5 p-0 sm:gap-5 lg:grid-cols-3">
          {[...(rows ?? []).flat(), ...photos].map((p) => (
            <li key={p.src} className="relative m-0 aspect-[5/2] overflow-hidden rounded-lg shadow-[0_8px_20px_-8px_rgb(17_28_49/0.45)] sm:rounded-xl sm:shadow-[0_12px_28px_-10px_rgb(17_28_49/0.45)]">
              <SiteImage
                src={p.src}
                width={p.width}
                height={p.height}
                alt={p.alt}
                sizes="(min-width: 1024px) 26rem, 50vw"
                className="absolute inset-0 m-0 h-full w-full object-cover object-top"
              />
            </li>
          ))}
        </ul>
        <Prose html={html} className="closing-verse" />
      </section>
    </Reveal>
  );
}

/** Renders any CMS page by slug. Gated pages prompt for sign-in instead of 404ing. */
export default function ContentPage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<Page | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api<{ page: Page }>(`/pages/${slug}`)
      .then((r) => !cancelled && setPage(r.page))
      .catch((err: ApiError) => !cancelled && setError(err))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (page) document.title = `${page.seoTitle ?? page.title} — FTTMY`;
    return () => {
      document.title = 'Full Time Training in Malaysia';
    };
  }, [page]);

  if (loading) return <Spinner />;

  if (error?.requiresAuth || error?.status === 403) {
    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center sm:px-6">
        <h1 className="font-display text-2xl font-semibold text-ink-900">Members only</h1>
        <p className="mt-3 text-lg text-ink-900">{error.message}</p>
        <Link
          to="/login"
          className="mt-8 inline-flex rounded-lg bg-ink-800 px-6 py-3 text-lg font-medium text-white transition-colors hover:bg-ink-900"
        >
          Sign in
        </Link>
      </div>
    );
  }

  if (error || !page) return <NotFound />;

  const brochure = slug ? BROCHURE_PAGES[slug] : undefined;
  const styled = brochure && !brochure.plain;
  // On brochure pages the quotations move down beside the closing photo, after
  // the smaller photos, so they lead into it.
  const quotesAt = brochure && !brochure.sectionPhotos ? page.contentHtml.indexOf('<blockquote') : -1;
  // A page with section photos is split before each section title and before
  // its closing hymn, so the photos can sit between the parts.
  const sections = brochure?.sectionPhotos
    ? page.contentHtml.split(/(?=<h2 class="section-title")|(?=<blockquote class="hymn")/)
    : null;
  const bodyHtml = quotesAt > -1 ? page.contentHtml.slice(0, quotesAt) : page.contentHtml;
  // Anything after the last quotation (a closing line) follows the photo.
  const quotesEnd = quotesAt > -1 ? page.contentHtml.lastIndexOf('</blockquote>') + '</blockquote>'.length : -1;
  const quotesHtml = quotesAt > -1 ? page.contentHtml.slice(quotesAt, quotesEnd) : '';
  const closingHtml = quotesAt > -1 ? page.contentHtml.slice(quotesEnd).trim() : '';

  return (
    <article>
      {/* Brochure pages open straight on their own section headings; the title
          stays for screen readers. */}
      {styled ? <h1 className="sr-only">{page.title}</h1> : <PageHeader title={page.title} />}
      <div
        className={`mx-auto w-full px-4 pb-14 sm:px-6 lg:px-10 lg:pb-16 xl:px-14 ${styled ? 'pt-8 lg:pt-10' : 'pt-5'}`}
      >
        {/* On a plain page the closing photo rises pale behind the last lines,
            so the text sits above it. */}
        {sections && brochure?.sectionPhotos ? (
          sections.map((part, i) => (
            <Fragment key={i}>
              {part.startsWith('<blockquote class="hymn"') && brochure.closing ? (
                <ClosingPanel {...brochure.closing} html={part} />
              ) : (
                <Prose html={part} />
              )}
              {i < sections.length - 1 && brochure.sectionPhotos?.[i] && (
                <PhotoRows rows={brochure.sectionPhotos[i]} className="mt-10" />
              )}
            </Fragment>
          ))
        ) : (
          <Prose html={bodyHtml} className={styled ? 'brochure' : brochure?.plain && brochure.photo ? 'letter relative z-10' : ''} />
        )}
        {brochure?.gallery && (
          /* A strip of smaller photos after the text, sharing one height so each
             stays whole, their edges lightly faded into the page; a lone photo is
             kept smaller. */
          <Reveal
            className={`mx-auto mt-12 gap-4 sm:gap-6 lg:mt-16 ${
              brochure.gallery.length > 2
                ? 'grid max-w-4xl grid-cols-2 items-center sm:flex sm:items-end'
                : `flex items-end ${brochure.gallery.length > 1 ? 'max-w-2xl' : 'max-w-sm sm:max-w-md'}`
            }`}
          >
            {brochure.gallery.map((p, i, all) => (
              <div
                key={p.src}
                /* Three or more go two to a row on phones; an odd last one spans both. */
                className={all.length > 2 && all.length % 2 === 1 && i === all.length - 1 ? 'col-span-2' : ''}
                style={{ flex: p.width / p.height }}
              >
                <SiteImage
                  src={p.src}
                  width={p.width}
                  height={p.height}
                  alt={p.alt}
                  sizes="(min-width: 768px) 30rem, 50vw"
                  className="h-auto w-full mask-x-from-92% mask-y-from-92%"
                />
              </div>
            ))}
          </Reveal>
        )}
        {quotesHtml && (
          <Reveal className="relative z-10 mt-14 lg:mt-20">
            <Prose html={quotesHtml} className="brochure brochure-quotes" />
          </Reveal>
        )}
        {brochure?.photo &&
          ((quotesHtml || brochure.plain) && !brochure.photoApart ? (
            /* As in the print: after quotations the photo is pale and rises up
               behind their last lines, fading out at the top and sides. */
            <Reveal className="-mt-16 sm:-mt-20 lg:-mt-24">
              <SiteImage
                src={brochure.photo.src}
                width={brochure.photo.width}
                height={brochure.photo.height}
                alt={brochure.photo.alt}
                sizes="(min-width: 768px) 48rem, 100vw"
                className="mx-auto h-auto w-full max-w-3xl opacity-75 mask-t-from-50% mask-b-from-85% mask-x-from-85%"
              />
            </Reveal>
          ) : (
            /* The whole photo, uncropped; its top melts into the page, the sides
               soften only at the very edges, and the bottom fades into a soft
               shadow beneath. */
            <Reveal className="mt-12 lg:mt-16">
              <div className={`relative isolate mx-auto ${brochure.photoApart ? 'max-w-3xl' : 'max-w-5xl'}`}>
                <SiteImage
                  src={brochure.photo.src}
                  width={brochure.photo.width}
                  height={brochure.photo.height}
                  alt={brochure.photo.alt}
                  sizes="(min-width: 1024px) 64rem, 100vw"
                  className="h-auto w-full mask-t-from-96% mask-b-from-92% mask-x-from-98%"
                />
                <div
                  aria-hidden
                  className="absolute inset-x-[6%] bottom-1 -z-10 h-6 rounded-[50%] bg-ink-900/25 blur-xl"
                />
              </div>
            </Reveal>
          ))}
        {closingHtml && (
          <Reveal className="mx-auto mt-10 max-w-4xl">
            <Prose html={closingHtml} className="brochure brochure-closing" />
          </Reveal>
        )}
      </div>
    </article>
  );
}
